import { useState } from "react";
import { FieldTitle, useInput, useTranslate } from "ra-core";
import {
  FormControl,
  FormError,
  FormField,
  FormLabel,
} from "@/components/admin/form";
import { Input } from "@/components/ui/input";
import { InputHelperText } from "@/components/admin/input-helper-text";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import {
  targetHoursFromInput,
  targetTimeInputFromHours,
  validateTargetTimeValue,
  type TargetTimeUnit,
} from "./stageConfigForm";

/**
 * Almikia Epic EP (TASK-105): "Tiempo objetivo" -- direccion enters the
 * stage's target time in hours OR days; the underlying `target_hours` field
 * always stores hours (an empty value stores `NULL`, "6 días" stores 144).
 * Switching the unit toggle keeps the number the user TYPED and reinterprets
 * it under the new unit (typing "3" then picking "Días" stores 72, i.e. 3
 * DAYS, never a conversion of the previous unit's value): the displayed
 * value only changes when the unit is later confirmed via the target_hours
 * prefill on reload ("144 h" opens as "6 días").
 */
export const TargetTimeInput = ({
  source = "target_hours",
}: {
  source?: string;
}) => {
  const translate = useTranslate();
  const { id, field, fieldState, isRequired } = useInput({
    source,
    validate: validateTargetTimeValue,
  });

  const initial = targetTimeInputFromHours(field.value);
  const [unit, setUnit] = useState<TargetTimeUnit>(initial.unit);
  const [displayValue, setDisplayValue] = useState<string>(
    initial.value != null ? String(initial.value) : "",
  );

  const handleUnitChange = (nextUnit: string) => {
    if (!nextUnit || nextUnit === unit) return;
    setUnit(nextUnit as TargetTimeUnit);
    // The typed number is REINTERPRETED under the new unit, never converted:
    // typing "3" then picking "Días" stores 72 (3 days), not 3 hours re-expressed
    // as a fraction of a day.
    field.onChange(
      targetHoursFromInput(nextUnit as TargetTimeUnit, displayValue),
    );
  };

  const handleValueChange = (nextValue: string) => {
    setDisplayValue(nextValue);
    field.onChange(targetHoursFromInput(unit, nextValue));
  };

  return (
    <FormField id={id} name={field.name}>
      <FormLabel>
        <FieldTitle
          label="resources.deal_stage_configs.fields.target_hours"
          source={source}
          isRequired={isRequired}
        />
      </FormLabel>
      <div className="flex gap-2 items-center flex-wrap">
        <FormControl>
          <Input
            type="number"
            min={1}
            step={1}
            className="w-24 tabular-nums"
            value={displayValue}
            onChange={(event) => handleValueChange(event.target.value)}
            onBlur={field.onBlur}
          />
        </FormControl>
        <ToggleGroup
          type="single"
          value={unit}
          onValueChange={handleUnitChange}
          variant="outline"
        >
          <ToggleGroupItem value="hours" className="min-h-11 min-w-11">
            {translate("resources.deal_stage_configs.target_time.unit_hours")}
          </ToggleGroupItem>
          <ToggleGroupItem value="days" className="min-h-11 min-w-11">
            {translate("resources.deal_stage_configs.target_time.unit_days")}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <InputHelperText
        helperText={
          fieldState.error
            ? false
            : "resources.deal_stage_configs.target_time.help"
        }
      />
      <FormError />
    </FormField>
  );
};
